import { useCallback, useContext, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { CgClose, CgPen } from 'react-icons/cg';
import * as yup from 'yup';
import {
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Dialog,
  DialogOpenChangeDetails,
  HStack,
  IconButton,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  useApplyTemplateRoleContact,
  useSaveRecipient,
  useSignatureRecipient,
  useSignatureTemplateRoleContacts,
} from '~/core/shared/api/signature';
import { FormField, Input, Select } from '~/core/shared/components';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useFormResolver, useRequiredParams } from '~/core/shared/hooks';
import {
  Nullable,
  Recipient,
  RecipientField,
  Template,
  TemplateInstance,
  TemplateRole,
  TemplateRoleContact,
} from '~/core/shared/types';
import { ApplicationContext } from '../application/Layout.component';
import { DocumentField } from '../form/DocumentField.component';

type TemplateInstanceFormProps = {
  instance: TemplateInstance;
  template?: Template;
  onDelete: (instance: TemplateInstance) => Promise<void>;
};

type InstanceRoleFormProps = {
  instance: TemplateInstance;
  role: TemplateRole;
  close: () => void;
};

type TemplateInstanceFormValues = {
  email: string;
  name: string;
  fields: { [key: string]: Nullable<string | boolean> };
};

const instanceRoleFormValidationSchema = yup.object({
  email: yup.string().trim().email().required(),
  name: yup.string().trim().required(),
  fields: yup.object(),
});

const InstanceRoleForm = ({ instance, role, close }: InstanceRoleFormProps) => {
  const { applyTemplateRoleContact } = useApplyTemplateRoleContact();
  const { saveRecipient } = useSaveRecipient();
  const resolver = useFormResolver<TemplateInstanceFormValues>(
    instanceRoleFormValidationSchema,
  );
  const form = useForm<TemplateInstanceFormValues>({
    resolver,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const { uuid } = useRequiredParams(['uuid']);
  const { definition } = useContext(ApplicationContext);

  const { recipient } = useSignatureRecipient({
    instance_id: instance.id,
    role_id: role.id,
  });

  const { contacts = EMPTY_ARR as TemplateRoleContact[] } =
    useSignatureTemplateRoleContacts(
      role.id,
      { definition },
      { document_id: uuid },
      { enabled: !!definition },
    );

  const setRecipientFormValues = (recipientData: Recipient) => {
    form.setValue('email', recipientData.email);
    form.setValue('name', recipientData.name);

    recipientData.fields.forEach((field: RecipientField) => {
      form.setValue(`fields.${field.field_id}`, field.value);
    });
  };

  useEffect(() => {
    if (recipient) {
      setRecipientFormValues(recipient);
    }
  }, [recipient]);

  const applyRecipient = async (documentId: string) => {
    const templatedRecipient = await applyTemplateRoleContact({
      roleId: role.id,
      documentParams: { definition },
      params: { document_id: documentId },
    });

    if (templatedRecipient) {
      setRecipientFormValues(templatedRecipient);
    }
  };

  const onSubmit = async (values: FieldValues) => {
    setIsProcessing(true);

    try {
      const result = await saveRecipient({
        instance_id: instance.id,
        role_id: role.id,
        ...values,
      });

      if (result) {
        close();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog.Positioner>
      <Dialog.Content>
        <Dialog.Header>Edit {role.title}</Dialog.Header>
        <Dialog.CloseTrigger asChild>
          <CloseButton />
        </Dialog.CloseTrigger>
        <Dialog.Body>
          <Box p={4}>
            <FormProvider {...form}>
              <Stack
                key={`role-${role.id}`}
                as="form"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <Text fontSize="lg">{role.title}</Text>
                <Select
                  name="recipients"
                  onChange={(e) =>
                    applyRecipient(e.target.value).then(() => {
                      e.target.value = '';
                    })
                  }
                  options={contacts.map((contact) => ({
                    value: contact.id,
                    label: contact.name,
                  }))}
                  placeholder="Recipient..."
                />
                <Stack>
                  <FormField
                    component={Input}
                    label="Recipient email"
                    name="email"
                    required
                  />
                  <FormField
                    component={Input}
                    label="Recipient name"
                    name="name"
                    required
                  />
                  {role.fields
                    .filter((field) => !field.is_hidden)
                    .map((field) => (
                      <DocumentField
                        key={`fields.${field.id}`}
                        field={{
                          id: field.id,
                          type: field.field_type,
                          label: field.label,
                          key: `fields.${field.id}`,
                          required: field.is_required,
                          fieldType: 'input',
                        }}
                      />
                    ))}
                </Stack>
                <Button loading={isProcessing} type="submit">
                  Save
                </Button>
              </Stack>
            </FormProvider>
          </Box>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Positioner>
  );
};

export const TemplateInstanceForm = ({
  instance,
  template,
  onDelete,
}: TemplateInstanceFormProps) => {
  const [selectedRole, setSelectedRole] = useState<TemplateRole | undefined>();
  // TODO: move to onClose prop of useOpenClose
  const onOpenChange = useCallback(({ open }: DialogOpenChangeDetails) => {
    if (open) return;
    setSelectedRole(undefined);
  }, []);

  return (
    <Stack key={`instance-${instance.id}`}>
      <HStack alignItems="flex-start" gap={8}>
        <HStack>
          <Box flexGrow={1}>
            <Text fontSize="xl">{template?.title ?? '[Deleted Template]'}</Text>
          </Box>
          <ButtonGroup fontSize="lg">
            {template &&
              (template.roles || []).map((role) => (
                <Button
                  key={role.id}
                  colorPalette="gray"
                  onClick={() => setSelectedRole(role)}
                  size="sm"
                  variant="outline"
                >
                  <CgPen />
                  {role.title}
                </Button>
              ))}
            <IconButton
              aria-label="Delete template"
              colorPalette="red"
              onClick={() =>
                window.confirm('Are you sure?') && onDelete(instance)
              }
              size="sm"
              variant="outline"
            >
              <CgClose size={18} />
            </IconButton>
          </ButtonGroup>
        </HStack>
        <Dialog.Root onOpenChange={onOpenChange} open={!!selectedRole}>
          <Dialog.Backdrop />
          {selectedRole && (
            <InstanceRoleForm
              close={() => setSelectedRole(undefined)}
              instance={instance}
              role={selectedRole}
            />
          )}
        </Dialog.Root>
      </HStack>
    </Stack>
  );
};
